/**
 * Scroll-driven progress for the Our journey timeline spine.
 */
class JourneyTimeline extends HTMLElement {
  /** @type {(() => void) | null} */
  #teardown = null;

  connectedCallback() {
    if (this.#teardown) return;

    const progress = this.querySelector('[data-journey-spine-progress]');
    const track = this.querySelector('[data-journey-spine-track]');
    const list = this.querySelector('.journey-timeline__list');
    const entries = this.querySelectorAll('.journey-timeline__entry');

    if (!(progress instanceof HTMLElement) || !(track instanceof HTMLElement) || !(list instanceof HTMLElement)) {
      return;
    }

    if (entries.length === 0) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /**
     * @param {HTMLElement} year
     * @returns {number}
     */
    const getDotAnchorRatio = (year) => {
      const anchor = parseFloat(
        getComputedStyle(year).getPropertyValue('--journey-timeline-dot-anchor') ||
          getComputedStyle(this).getPropertyValue('--journey-timeline-dot-anchor')
      );
      return Number.isFinite(anchor) ? anchor / 100 : -0.3;
    };

    const getSpineTailRatio = () => {
      const tail = parseFloat(getComputedStyle(this).getPropertyValue('--journey-timeline-spine-tail'));
      return Number.isFinite(tail) ? tail / 100 : 0.18;
    };

    /**
     * @param {HTMLElement} year
     * @param {DOMRect} rootRect
     * @returns {number}
     */
    const getDotOffsetTop = (year, rootRect) => {
      const rect = year.getBoundingClientRect();
      return rect.top + rect.height * getDotAnchorRatio(year) - rootRect.top;
    };

    const syncTrackGeometry = () => {
      const firstEntry = entries[0];
      const lastEntry = entries[entries.length - 1];
      const firstYear = firstEntry?.querySelector('.journey-timeline__year');
      const lastYear = lastEntry?.querySelector('.journey-timeline__year');

      if (
        !(firstEntry instanceof HTMLElement) ||
        !(lastEntry instanceof HTMLElement) ||
        !(firstYear instanceof HTMLElement) ||
        !(lastYear instanceof HTMLElement)
      ) {
        return;
      }

      const rootRect = this.getBoundingClientRect();
      const firstDot = getDotOffsetTop(firstYear, rootRect);
      const lastEntryRect = lastEntry.getBoundingClientRect();
      const trackEnd = lastEntryRect.bottom - rootRect.top - lastEntryRect.height * getSpineTailRatio();

      track.style.top = `${firstDot}px`;
      track.style.height = `${Math.max(0, trackEnd - firstDot)}px`;

      const dotSize = parseFloat(getComputedStyle(this).getPropertyValue('--journey-timeline-dot-size')) || 16;
      const dotLineGap = parseFloat(getComputedStyle(this).getPropertyValue('--journey-timeline-dot-line-gap')) || 6;
      const maskEnd = firstDot - dotSize / 2 - dotLineGap;

      this.style.setProperty('--journey-spine-mask-top', `${Math.max(0, maskEnd)}px`);
    };

    const updateProgress = () => {
      syncTrackGeometry();

      const trackHeight = track.offsetHeight;
      if (trackHeight <= 0) return;

      if (prefersReducedMotion) {
        progress.style.height = '100%';
        entries.forEach((entry) => {
          entry.classList.add('journey-timeline__entry--reached');
        });
        return;
      }

      const trackRect = track.getBoundingClientRect();
      const trackStart = trackRect.top + window.scrollY;
      const readLine = window.scrollY + window.innerHeight * 0.5;
      const progressRatio = Math.min(1, Math.max(0, (readLine - trackStart) / trackHeight));

      progress.style.height = `${progressRatio * 100}%`;

      entries.forEach((entry) => {
        const year = entry.querySelector('.journey-timeline__year');
        if (!(year instanceof HTMLElement)) return;

        const yearRect = year.getBoundingClientRect();
        const dotCenter = yearRect.top + yearRect.height * getDotAnchorRatio(year);
        const reached = dotCenter <= window.innerHeight * 0.55;
        entry.classList.toggle('journey-timeline__entry--reached', reached);
      });
    };

    let frame = 0;
    const scheduleUpdate = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        updateProgress();
      });
    };

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(list);
    entries.forEach((entry) => {
      resizeObserver.observe(entry);
    });

    if ('ResizeObserver' in window) {
      const images = list.querySelectorAll('img');
      images.forEach((image) => {
        if (image.complete) return;
        image.addEventListener('load', scheduleUpdate, { once: true });
      });
    }

    updateProgress();

    this.#teardown = () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      resizeObserver.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }

  disconnectedCallback() {
    this.#teardown?.();
    this.#teardown = null;
  }
}

if (!customElements.get('journey-timeline')) {
  customElements.define('journey-timeline', JourneyTimeline);
}
